import type { Seo, SeoAlternateLink } from '~/types/layouts/seo';
import {
  getRobots,
  getArticleAuthor,
  getArticleTag,
  getPageUrlTemplatedTag,
  getMetaTitleTemplatedTag,
  getMetaDescriptionTemplatedTag,
  getOgImageAlt,
  getOgImageUrl,
  getOgImageSecureUrl,
  getSiteUrlTemplatedTag,
  getTwitterImageAlt,
} from './utils';

export default function useUpdatePageMeta(
  data: Seo | undefined | null,
  pagePath: string,
) {
  const { public: publicEnv } = useRuntimeConfig();

  const payload = getUseSeoMetaPayload({
    data,
    siteUrl: publicEnv.siteUrl,
    pagePath,
  });

  useServerSeoMeta(payload);

  if (data?.canonical || data?.jsonLD || data?.alternateLinks?.length) {
    useHead({
      link: getLinksData({
        data,
        siteUrl: publicEnv.siteUrl,
        pagePath,
      }),
      script: getJsonLdData(data),
    });
  }
}

function getLinksData({
  data,
  siteUrl,
  pagePath,
}: {
  data: Seo | undefined;
  siteUrl: string;
  pagePath: string;
}) {
  const canonicalLink = getCanonicalLinkData({
    data,
    siteUrl,
    pagePath,
  });

  const alternateLinks = data?.alternateLinks?.map(getAlternateLinkData) || [];

  return canonicalLink ? [canonicalLink, ...alternateLinks] : alternateLinks;
}

function getUseSeoMetaPayload({
  data,
  siteUrl,
  pagePath,
}: {
  data: Seo | undefined | null;
  siteUrl: string;
  pagePath: string;
}) {
  const payload = {
    ...data,

    robots: getRobots(data),

    ...data?.ogTags,
    ...data?.articleTags,
    ...data?.twitterTags,

    ogTitle: getMetaTitleTemplatedTag({
      value: data?.ogTags?.ogTitle,
      data,
    }),
    ogDescription: getMetaDescriptionTemplatedTag({
      value: data?.ogTags?.ogDescription,
      data,
    }),
    ogUrl: getPageUrlTemplatedTag({
      value: data?.ogTags?.ogUrl,
      siteUrl,
      pagePath,
    }),
    ogImage: getOgImageUrl(data),
    ogImageSecureUrl: getOgImageSecureUrl(data),
    ogImageAlt: getOgImageAlt(data),

    articleAuthor: getArticleAuthor(data),
    articleTag: getArticleTag(data),

    twitterSite: getPageUrlTemplatedTag({
      value: data?.twitterTags?.twitterSite,
      siteUrl,
      pagePath,
    }),
    twitterTitle: getMetaTitleTemplatedTag({
      value: data?.twitterTags?.twitterTitle,
      data,
    }),
    twitterDescription: getMetaDescriptionTemplatedTag({
      value: data?.twitterTags?.twitterDescription,
      data,
    }),
    twitterCreator: getSiteUrlTemplatedTag({
      value: data?.twitterTags?.twitterCreator,
      siteUrl,
    }),
    twitterDomain: getSiteUrlTemplatedTag({
      value: data?.twitterTags?.twitterDomain,
      siteUrl,
    }),
    twitterImageAlt: getTwitterImageAlt(data),
  };

  delete payload.id;
  delete payload.canonical;
  delete payload.ogTags;
  delete payload.articleTags;
  delete payload.twitterTags;
  delete payload.jsonLD;

  return payload;
}

function getCanonicalLinkData({
  data,
  siteUrl,
  pagePath,
}: GetCanonicalLinkDataPayload) {
  const href = getPageUrlTemplatedTag({
    value: data?.canonical,
    siteUrl,
    pagePath,
  });

  if (href) {
    return {
      rel: 'canonical',
      href,
    };
  }
}

interface GetCanonicalLinkDataPayload {
  data: Seo | undefined;
  siteUrl: string;
  pagePath: string;
}

function getAlternateLinkData(link: SeoAlternateLink) {
  return {
    rel: 'alternate',
    href: link.href,
    hreflang: link.hreflang,
  };
}

function getJsonLdData(data: Seo | undefined) {
  if (data?.jsonLD) {
    return [
      {
        type: 'application/ld+json',
        textContent: JSON.stringify(data?.jsonLD),
      },
    ];
  }
}
